<template>
    <SimpleSlider tag="nav" content-tag="ul" class="h-full">
        <li v-for="(menu, mi) in menus" :key="mi"
            class="navigation-item inline-flex items-center h-full relative mr-20 lg:mr-40"
            :class="{active: megaMenuShown && activeMenu === menu}"
            @mouseenter="mouseEnter(menu)"
            @mouseleave="mouseLeave(menu)">
            <CultureAwareRouterLink :to="menu.url"
                                    class="navigation-item-link text-13 whitespace-nowrap select-none text-current cursor-pointer relative flex items-center justify-center h-full font-medium"
                                    @click="$emit('closeMegaMenu')">
                <span>
                    {{ menu.name }}
                </span>
            </CultureAwareRouterLink>
        </li>

        <li v-if="campaignLink" class="flex-1 flex justify-end">
            <div class="navigation-item inline-flex items-center h-full relative">
                <CultureAwareRouterLink :to="campaignLink.url"
                                        class="navigation-item-link flex items-center justify-center text-13 whitespace-nowrap select-none text-current cursor-pointer relative h-full font-medium"
                                        @mouseenter="$emit('closeMegaMenu')">
                    <CampaignIcon :campaign-code="campaignCode" class="mr-8"
                                  :fill="true" :original="false" width="15"
                                  height="15"/>
                    <span>{{ campaignLink.name }}</span>
                </CultureAwareRouterLink>
            </div>
        </li>

        <template #left-arrow="{ active }">
            <div v-if="active" class="prev-arrow-item absolute -left-10 h-full w-50 flex justify-start items-center" :style="prevNavSliderStyle">
                <CIcon name="arrow-left" width="15" height="15"/>
            </div>
        </template>

        <template #right-arrow="{ active }">
            <div v-if="active" class="next-arrow-item absolute -right-10 h-full w-50 flex justify-end items-center" :style="nextNavSliderStyle">
                <CIcon name="arrow-right" width="15" height="15"/>
            </div>
        </template>
    </SimpleSlider>
</template>

<script setup lang="ts">
import { computed, PropType, StyleValue } from 'vue';
import { MenuItem } from '@/types/apiServerContract';
import SimpleSlider from '@/core/ui-components/SimpleSlider.vue';
import serverContextService from '@/core/serverContext.service';
import CampaignIcon from '@/project/product/campaigns/CampaignIcon.vue';
import themeService from '@/project/app/themes/theme.service';

const props = defineProps({
    menus: { type: Array as PropType<MenuItem[] | null>, required: true },
    megaMenuShown: { type: Boolean, required: true },
    activeMenu: { type: Object as PropType<MenuItem | null>, required: false, default: null }
});

const emit = defineEmits<{
    (e: 'activeMenuChanged', menu: MenuItem): void;
    (e: 'cancelMegaMenuNavigation', menu: MenuItem): void;
    (e: 'closeMegaMenu'): void;
}>();

const mouseEnter = (menu: MenuItem) => {
    emit('activeMenuChanged', menu);
};

const mouseLeave = (menu: MenuItem) => {
    emit('cancelMegaMenuNavigation', menu);
};

const campaignCode = computed(() => serverContextService?.settings?.menuCampaignIcon);

const campaignLink = computed(() => serverContextService?.settings?.menuCampaignLink);

const prevNavSliderStyle = computed((): StyleValue => {
    if (themeService.theme?.menuColor) {
        const backgroundGradient = `linear-gradient(90deg, ${themeService.theme.menuColor} 0%, ${themeService.theme.menuColor}00 100%)`;
        return {
            background: backgroundGradient,
            color: themeService.getColor(themeService.theme.menuColor)
        };
    }
    return {};
});

const nextNavSliderStyle = computed((): StyleValue => {
    if (themeService.theme?.menuColor) {
        const backgroundGradient = `linear-gradient(90deg, ${themeService.theme.menuColor}00 0%, ${themeService.theme.menuColor} 100%)`;
        return {
            background: backgroundGradient,
            color: themeService.getColor(themeService.theme.menuColor)
        };
    }
    return {};
});
</script>

<style lang="less" scoped>
.navigation-item {
    &:after {
        @apply absolute bg-current w-full block bottom-0;
        content: "";
        height: 3px;
        transition: transform 0.3s ease;
        transform: scaleX(0);
    }

    &.active:after, &:hover:after {
        transform: scaleX(1);
    }

    &:last-of-type {
        margin-right: 0;
    }
}

.navigation-item-link {
    &.router-link-active {
        @apply font-bold;
    }
}

:deep(.prev-arrow-item) {
    @apply bg-gradient-to-r from-blue-500 to-blue-500/0 oprema:from-opremayellow-500 oprema:to-opremayellow-500/0 hesa:from-hesared-500 hesa:to-hesared-500/0 dias:from-diasblue-500 hesa:to-diasblue-500/0;
}

:deep(.next-arrow-item) {
    @apply bg-gradient-to-r from-blue-500/0 to-blue-500 oprema:from-opremayellow-500/0 oprema:to-opremayellow-500 hesa:from-hesared-500/0 hesa:to-hesared-500 dias:from-diasblue-500/0 dias:to-diasblue-500;
}
</style>
